/**
 * Framework-agnostic component URL resolver registry.
 *
 * The navigation engine dynamic-imports SDX component bundles
 * before it swaps the DOM so the browser's module cache is warm
 * by the time the mount script runs. Figuring out *which* URL
 * serves a given component ID is framework-specific - React, Vue,
 * and Svelte each store their dist paths and dev-server URLs in
 * their own drupalSettings namespace - so the resolution logic
 * cannot live in sdx_router itself without leaking framework
 * knowledge into a framework-agnostic module.
 *
 * Instead, sdx_router exposes this registry and each framework
 * module registers its own resolver from its mount script. The
 * navigation engine iterates every registered resolver in turn
 * and uses the first URL that comes back non-null. No hardcoded
 * framework list, no cross-module coupling, and new framework
 * modules extend the pipeline simply by registering.
 *
 * Framework modules register via the public API exposed on
 * `Drupal.sdxNavigation.registerComponentResolver(resolver)`,
 * which returns an unregister callback for teardown symmetry.
 */

export type ComponentResolver = (componentId: string) => string | null;

export class ComponentResolverRegistry {
  private readonly resolvers: ComponentResolver[] = [];

  register(resolver: ComponentResolver): () => void {
    this.resolvers.push(resolver);
    return () => {
      const idx = this.resolvers.indexOf(resolver);
      if (idx !== -1) {
        this.resolvers.splice(idx, 1);
      }
    };
  }

  /**
   * Returns the first resolved URL from any registered resolver,
   * or null when none of them can resolve the component. The
   * first-match policy means registration order matters: a
   * framework module that registers early wins over later
   * registrations, which mirrors the way Drupal's hook ordering
   * works.
   */
  resolve(componentId: string): string | null {
    for (const resolver of this.resolvers) {
      const url = resolver(componentId);
      if (typeof url === 'string' && url.length > 0) {
        return url;
      }
    }
    return null;
  }

  /**
   * True when at least one framework module has registered.
   *
   * The navigation engine uses this to short-circuit the preload
   * pass on pages where no framework is active, avoiding a
   * needless Promise.all over an empty array.
   */
  hasResolvers(): boolean {
    return this.resolvers.length > 0;
  }
}
